"use client";

import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

import React, { useRef, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";

import Input from "@/components/ui/input";
import {
  SortableDragHandle,
  SortableItem,
  SortableList,
} from "@/components/ui/sortable-list";

import { cx } from "@/lib/tailwind-utils";

const MIN_COL_WIDTH = 60;
const DEFAULT_COL_WIDTH = 150;
const ROW_HEADER_WIDTH = 160;
// Room for the drag handle beside the row header.
const DRAG_HANDLE_WIDTH = 28;
const ROW_SORT_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement];

export interface TableGridValue {
  rows: string[];
  columns: string[];
  cells: string[][];
  widths: number[];
}

interface TableGridProps {
  rows: string[];
  columns: string[];
  cells?: string[][];
  widths?: number[];
  /** Fully read-only (static text, no controls). */
  disabled?: boolean;
  /** Render editable cell inputs (note fill). When false, cells render as blank
   *  placeholders — the template only defines the grid's shape, not its data. */
  editableCells?: boolean;
  /** First N row/column headers are fixed (used in the note so template-defined
   *  headers can't be renamed/removed; only provider-added ones can). */
  lockedRows?: number;
  lockedCols?: number;
  /** Allow drag-resizing column widths. */
  resizable?: boolean;
  /** Emits the full next value; the caller persists whatever it cares about. */
  onChange?: (next: TableGridValue) => void;
}

export function buildCells(
  rowCount: number,
  columnCount: number,
  saved: string[][] | undefined,
): string[][] {
  return Array.from({ length: rowCount }, (_, r) =>
    Array.from({ length: columnCount }, (_, c) => saved?.[r]?.[c] ?? ""),
  );
}

export function padWidths(widths: number[] | undefined, len: number): number[] {
  return Array.from({ length: len }, (_, c) => widths?.[c] ?? 0);
}

/** Reorders rows and their cells with one permutation, so a row's values move
 *  with its header. `order[i]` is the current index of the row that lands at
 *  position i. Columns and widths don't change. */
export function reorderRows(
  value: Pick<TableGridValue, "rows" | "cells">,
  order: number[],
): Pick<TableGridValue, "rows" | "cells"> {
  return {
    rows: order.map((i) => value.rows[i]),
    cells: order.map((i) => value.cells[i]),
  };
}

export const TableGrid: React.FC<TableGridProps> = ({
  rows,
  columns,
  cells: cellsProp,
  widths: widthsProp,
  disabled = false,
  editableCells = false,
  lockedRows = 0,
  lockedCols = 0,
  resizable = false,
  onChange,
}) => {
  const cells = buildCells(rows.length, columns.length, cellsProp);
  const widths = padWidths(widthsProp, columns.length);

  const dragRef = useRef<{
    col: number;
    startX: number;
    startW: number;
    w: number;
  } | null>(null);
  const [live, setLive] = useState<{ col: number; w: number } | null>(null);

  const emit = (over: Partial<TableGridValue>) =>
    onChange?.({ rows, columns, cells, widths, ...over });

  const setCell = (r: number, c: number, value: string) => {
    const next = buildCells(rows.length, columns.length, cells);
    next[r][c] = value;
    emit({ cells: next });
  };
  const addColumn = () =>
    emit({
      columns: [...columns, ""],
      cells: cells.map((row) => [...row, ""]),
      widths: [...widths, 0],
    });
  const removeColumn = (c: number) =>
    emit({
      columns: columns.filter((_, i) => i !== c),
      cells: cells.map((row) => row.filter((_, i) => i !== c)),
      widths: widths.filter((_, i) => i !== c),
    });
  const setColumnHeader = (c: number, value: string) =>
    emit({ columns: columns.map((h, i) => (i === c ? value : h)) });
  const addRow = () =>
    emit({
      rows: [...rows, ""],
      cells: [...cells, Array.from({ length: columns.length }, () => "")],
    });
  const removeRow = (r: number) =>
    emit({
      rows: rows.filter((_, i) => i !== r),
      cells: cells.filter((_, i) => i !== r),
    });
  const setRowHeader = (r: number, value: string) =>
    emit({ rows: rows.map((h, i) => (i === r ? value : h)) });

  // Only unlocked rows move, and only among themselves: in the note the first
  // `lockedRows` are the template's rows, locked by position, so letting a
  // provider row move above them would lock it and unlock a template row.
  const sortable = !disabled && !!onChange;
  const rowIds = rows.map((_, r) => `row-${r}`);
  const sortableRowIds = rowIds.slice(lockedRows);
  const reorder = (ids: string[]) =>
    emit(
      reorderRows({ rows, cells }, [
        ...rowIds.slice(0, lockedRows).map((_, r) => r),
        ...ids.map((id) => rowIds.indexOf(id)),
      ]),
    );

  const onResizeDown = (c: number, e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement as HTMLElement;
    const startW = widths[c] > 0 ? widths[c] : th.getBoundingClientRect().width;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { col: c, startX: e.clientX, startW, w: startW };
    setLive({ col: c, w: startW });
  };
  const onResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const w = Math.max(MIN_COL_WIDTH, d.startW + (e.clientX - d.startX));
    d.w = w;
    setLive({ col: d.col, w });
  };
  const onResizeUp = () => {
    const d = dragRef.current;
    if (d) {
      const next = padWidths(widths, columns.length);
      next[d.col] = d.w;
      emit({ widths: next });
    }
    dragRef.current = null;
    setLive(null);
  };
  const colWidth = (c: number): number => {
    if (live && live.col === c) return live.w;
    return widths[c] > 0 ? widths[c] : DEFAULT_COL_WIDTH;
  };

  const headerCls =
    "border border-border bg-background-secondary px-3 py-2 text-left text-caption font-medium text-foreground-secondary";

  const renderHeaderInput = (
    value: string,
    onChangeValue: (v: string) => void,
    onRemove: () => void,
    ariaLabel: string,
    placeholder: string,
  ) => (
    <div className="flex items-center gap-1">
      <Input
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        placeholder={placeholder}
        className="h-7 border-none bg-transparent px-1 text-caption font-medium shadow-none focus-visible:ring-1"
        aria-label={ariaLabel}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${value || ariaLabel}`}
        className="shrink-0 text-foreground-tertiary hover:text-destructive"
      >
        <FiX className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  const renderRowCells = (
    rowLabel: string,
    r: number,
    handle: React.ReactNode,
  ) => {
    const editable = !disabled && r >= lockedRows;
    return (
      <>
        <th scope="row" className={headerCls}>
          <div className="flex items-center gap-1">
            {handle}
            <div className="min-w-0 flex-1">
              {editable
                ? renderHeaderInput(
                    rowLabel,
                    (v) => setRowHeader(r, v),
                    () => removeRow(r),
                    `Row ${r + 1} header`,
                    "Row",
                  )
                : rowLabel}
            </div>
          </div>
        </th>
        {columns.map((_, c) => (
          <td key={c} className="border border-border p-0">
            {editableCells && !disabled ? (
              <Input
                value={cells[r]?.[c] ?? ""}
                onChange={(e) => setCell(r, c, e.target.value)}
                className="h-9 rounded-none border-none shadow-none focus-visible:ring-1 focus-visible:ring-inset"
                aria-label={`${rowLabel || `Row ${r + 1}`} — ${columns[c] || `Column ${c + 1}`}`}
              />
            ) : (
              <div className="min-h-9 whitespace-pre-wrap px-3 py-2 text-body text-foreground">
                {cells[r]?.[c] || (disabled ? "—" : "")}
              </div>
            )}
          </td>
        ))}
      </>
    );
  };

  const table = (
    <table
      className="border-collapse text-body"
      style={{
        tableLayout: "fixed",
        width: "max-content",
        minWidth: "100%",
      }}
    >
      <colgroup>
        <col
          style={{
            width: ROW_HEADER_WIDTH + (sortable ? DRAG_HANDLE_WIDTH : 0),
          }}
        />
        {columns.map((_, c) => (
          <col key={c} style={{ width: colWidth(c) }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className={headerCls} />
          {columns.map((column, c) => {
            const editable = !disabled && c >= lockedCols;
            return (
              <th key={c} className={cx(headerCls, "relative")}>
                {editable ? (
                  renderHeaderInput(
                    column,
                    (v) => setColumnHeader(c, v),
                    () => removeColumn(c),
                    `Column ${c + 1} header`,
                    "Column",
                  )
                ) : (
                  <span>{column}</span>
                )}
                {resizable && !disabled && (
                  <div
                    onPointerDown={(e) => onResizeDown(c, e)}
                    onPointerMove={onResizeMove}
                    onPointerUp={onResizeUp}
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary/40"
                    aria-hidden
                  />
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((rowLabel, r) =>
          sortable && r >= lockedRows ? (
            <SortableItem
              key={rowIds[r]}
              id={rowIds[r]}
              as="tr"
              className="bg-background"
            >
              {renderRowCells(
                rowLabel,
                r,
                <SortableDragHandle
                  ariaLabel={`Reorder ${rowLabel || `row ${r + 1}`}`}
                  className="h-7 w-5 shrink-0"
                />,
              )}
            </SortableItem>
          ) : (
            <tr key={rowIds[r]}>
              {renderRowCells(
                rowLabel,
                r,
                sortable && <span className="w-5 shrink-0" aria-hidden />,
              )}
            </tr>
          ),
        )}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-2">
      {sortable ? (
        <SortableList
          items={sortableRowIds}
          onReorder={reorder}
          modifiers={ROW_SORT_MODIFIERS}
          className="overflow-x-auto"
        >
          {table}
        </SortableList>
      ) : (
        <div className="overflow-x-auto">{table}</div>
      )}
      {!disabled && onChange && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-caption text-foreground-secondary hover:bg-hover"
          >
            <FiPlus className="h-3.5 w-3.5" /> Add row
          </button>
          <button
            type="button"
            onClick={addColumn}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-caption text-foreground-secondary hover:bg-hover"
          >
            <FiPlus className="h-3.5 w-3.5" /> Add column
          </button>
        </div>
      )}
    </div>
  );
};
